<template>
    <popup-modal
        customClasses="w-900 max-w-full"
        :close-on-click="closeOnClick"
        :title="$t('mailer.header_invitation_popup')"
        @close="$emit('close')"
        :confirm="true"
        :dirty="interviewForm.isDirty() || mailForm.isDirty()"
    >
        <div>
            <div v-if="!fetchingTemplates">

                <h2 class="first-letter:uppercase font-semibold text-body mb-16">
                    {{ $t('interview.details') }}
                </h2>

                <div class="flex">
                    <div class="w-1/2 pt-16 mr-32">
                        <simple-input v-model="interviewForm.name"
                                      class="mb-16 first-letter:uppercase">
                            {{ $t('interview.name') }}
                        </simple-input>
                    </div>
                    <div class="w-1/2 pt-16"></div>
                </div>

                <interview-type-fields v-model="interviewForm">
                    <div class="w-1/2 flex justify-between">
                        <div v-for="type in subTypes">
                            <label class="mb-8 text-body-sm flex items-center">
                                <span class="first-letter:uppercase font-semibold text-black">
                                    {{ $t(`subscriptions.subscribed_${type}_title`) }}
                                </span>
                            </label>
                            <div class="flex items-center">
                                <div :class="subscriptionList(type).length ? 'mr-16' : ''" class="flex">
                                    <template v-for="sub in subscriptionList(type)">
                                        <tooltip
                                            v-if="sub.user"
                                            :key="sub.user.id"
                                            :content="sub.user.name"
                                            class="-mr-8"
                                            position="bottom"
                                        >
                                            <avatar
                                            :image="sub.user.image_url"
                                                    size="small"
                                                    class="cursor-default border border-white"
                                                    >{{ sub.user.initials }}
                                            </avatar>
                                        </tooltip>
                                    </template>
                                </div>
                                <button
                                    class="h-32 w-32 p-2 flex font-semibold items-center justify-center rounded-32 bg-primary hover:bg-primary-80 text-white trans-ease"
                                    @click="manageSubscriptions"
                                >
                                    <svg-icon type="add" />
                                </button>
                            </div>
                        </div>
                    </div>
                </interview-type-fields>

                <div v-if="hasAccessToCalendar">
                    <interview-mode-manager
                        :form="interviewForm"
                        :subs="requiredSubscriptions"
                        :alerts="timeslotsAlerts"
                        :has-bulk-timeslots="totalApplications > 1"
                        :can-manage="true"
                        :creating="true"
                        @change="interviewForm.mode = $event"
                    ></interview-mode-manager>
                </div>
                <interview-timeslots
                    v-else
                    v-model="interviewForm"
                    :creating="true"
                    :alerts="timeslotsAlerts"
                    :has-bulk-timeslots="totalApplications > 1"
                />

                <div>
                    <h2 class="first-letter:uppercase font-semibold text-body mb-16">
                        {{ $t('interview.email.title') }}
                    </h2>

                    <checkbox v-if="interviewForm.slot_confirmed"
                              v-model="interviewForm.send_confirmation_mail"
                              class="mr-8 mb-16 whitespace-nowrap"
                    >
                        <span class="font-normal">{{ $t('interview.send_confirmation_mail') }}</span>
                    </checkbox>

                    <div v-show="!interviewForm.slot_confirmed || (interviewForm.slot_confirmed && interviewForm.send_confirmation_mail)">
                        <banner
                            v-if="amountValidEmails === 0"
                            danger
                            :description="$tc('mail.warnings.not-allowed', applications.length)"
                            inline
                        />
                        <div v-else-if="amountInvalidEmails > 0" class="font-semibold text-semantic-warning mb-16 p-16 bg-semantic-warning-light border border-semantic-warning rounded">
                            {{ $t('mail.warnings.missing-emails') }}
                            <ul class="pt-4 flex flex-wrap text-primary font-normal">
                                <li
                                    v-for="application in applications.filter(i => !i.email)"
                                    :key="application.id"
                                    class="inline-flex items-center mr-8 my-4"
                                >
                                    <avatar
                                        :color="application.stageColor"
                                        :image="application.image_url"
                                        size="small"
                                        class="mr-4"
                                    >
                                        {{ application.initials }}
                                    </avatar>
                                    <span>{{ application.fullName }}</span>
                                </li>
                            </ul>
                        </div>

                        <div class="mb-16">
                            <Recipients
                                :is-bulk-based-on-count="isBulkBasedOnCount"
                                :applications-count="applicationsCount"
                                :total-applications="totalApplications"
                                :applications="applications"
                                :fetching-data="fetchingData"
                                :can-edit="context !== 'single'"
                                @updateApplicants="updateApplications"
                            />
                        </div>

                        <label for="bcc-search-input" class="mb-8 flex items-center text-black space-x-2">
                            <span class="font-semibold">{{ $t('mailer.add_bcc') }}</span>
                            <span class="font-semibold">({{ $t('general.optional') }})</span>
                        </label>
                        <bcc-fields v-model="mailForm.bcc" class="mb-16"></bcc-fields>


                        <select-dropdown
                            static-id="interviewTemplateNameDropdown"
                            v-model="selectedTemplate"
                            :auto-translate="false"
                            :options="templateChoices"
                            no-auto-sort
                            class="mb-16 w-full"
                            @change="hydrateFromTemplate"
                        >
                            {{ $t('mailer.choose_template') }}


                            <tabs
                                :active-key="templateLocale"
                                class="inline-flex w-full"
                                :items="languages"
                                scroll
                                v-if="languages"
                                @set-active="hydrateFromTemplate"
                            />
                            <span v-if="applications[0].locale !== this.templateLocale">{{ $t('interview.error_candidate_template_language') }}</span>
                        </select-dropdown>

                        <simple-input v-show="!interviewForm.slot_confirmed"
                                      v-model="mailForm.subject"
                                      class="mb-16"
                        >
                            {{ $t('mailer.subject') }}
                        </simple-input>

                        <wysiwyg v-show="!interviewForm.slot_confirmed"
                                 ref="content"
                                 v-model="mailForm.message"
                                 :placeholders="['application', 'company', 'user', 'interviewInvitation']"
                                 class="mb-16"
                                 with-links
                        >
                            {{ $t('mailer.description') }}

                            <template #after>
                                <div
                                    :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                                    class="flex p-16 border-t border-primary-20"
                                >
                                    <div class="text-body flex-grow">
                                        <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                        <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                                    </div>
                                    <select-dropdown
                                        v-model="selectedSignature"
                                        :auto-translate="false"
                                        :options="signatureChoices"
                                        class="min-w-240"
                                    />
                                </div>
                            </template>
                        </wysiwyg>


                        <div class="border border-primary-5 bg-primary-5 rounded-4 px-24 mb-16">

                            <simple-button
                                class="py-16"
                                :icon="showConfirmationForm ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
                                look="text"
                                @click="showConfirmationForm = !showConfirmationForm"
                            >
                                {{ $t('interview.confirm_message') }}
                            </simple-button>

                            <div v-show="showConfirmationForm">
                                <simple-input
                                    class="mb-16"
                                    :errors="invitationForm.errors.confirmation_subject"
                                    :input-label="$t('settings.email-templates.subject')"
                                    v-model="invitationForm.confirmation_subject"
                                />

                                <wysiwyg ref="confirmation_content"
                                         v-model="invitationForm.confirmation_message"
                                         :errors="invitationForm.errors.confirmation_message"
                                         :placeholders="['application', 'company', 'user', 'interviewInvitation', 'interviewInvitationConfirmed']"
                                         class="mb-16"
                                         with-links
                                >
                                    {{ $t('form.content') }}

                                    <template #after>
                                        <div
                                            :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                                            class="flex p-16 border-t border-primary-20"
                                        >
                                            <div class="text-body flex-grow">
                                                <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                                <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                                            </div>
                                            <select-dropdown
                                                v-model="selectedSignature"
                                                :auto-translate="false"
                                                :options="signatureChoices"
                                                class="min-w-240"
                                            ></select-dropdown>
                                        </div>
                                    </template>

                                </wysiwyg>
                            </div>
                        </div>

                        <file-upload v-if="!interviewForm.slot_confirmed" ref="fileUpload" v-model="mailForm.attachments" multiple @removed="removeFile">
                            <!--show exsisting files-->
                            <ul
                                class="rounded-sm overflow-hidden w-full mb-16">
                                <li
                                    v-for="attachment in mailForm.attachments"
                                    v-show="attachment.locale === active_locale"
                                    :key="'attachment-' + attachment.id"
                                    class="odd:bg-primary-5 relative"
                                >
                                    <a
                                        :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                                        :href="attachment.url"
                                        :target="extensions.includes(attachment.extension) ? '_blank' : null"
                                        class="group flex items-center p-16 cursor-pointer">
                                        <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachment.original_name }}</div>
                                        <div class="pl-16">
                                            <div class="h-32 w-32 flex items-center justify-center rounded-sm text-primary-80 trans-ease group-hover:text-primary mr-32">
                                                <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                                            </div>
                                        </div>
                                    </a>

                                    <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                                        <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-sm"
                                                @click="confirmDeleteAttachment(attachment)">
                                            <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </file-upload>
                    </div>
                </div>

                <div v-if="showInternalNotes">
                    <h2 class="first-letter:uppercase font-semibold text-body pt-16 mb-16">
                        {{ $t('interview.internal_notes') }}
                    </h2>

                    <wysiwyg v-model="interviewForm.internal_notes" class="mb-16"/>
                </div>

                <banner
                    v-if="!currentCompany.is_demo && currentCompany.in_trial"
                    :description="$t('applications.no_mails_in_free_trial', {email: currentCompany.email})"
                    inline
                    warning
                />

                <div class="flex items-center justify-between pt-32">
                    <action-button
                        icon="add-circle"
                        icon-before
                        icon-color="secondary"
                        v-if="!showInternalNotes"
                        @click="showInternalNotes = !showInternalNotes"
                    >
                        {{ $t('interims.internal_notes.add_new') }}
                    </action-button>

                    <simple-button
                        class="ms-auto"
                        :disabled="amountValidEmails === 0"
                        :loading="sendingInvitation"
                        display="secondary"
                        look="primary"
                        icon="send"
                        @click="getBulkApplicationKey"
                    >
                        {{ submitButtonLabel }}
                    </simple-button>
                </div>
            </div>

            <div v-else class="flex items-center justify-center pt-48 mb-32">
                <loading></loading>
            </div>
        </div>

        <subscriber-manager
            v-if="managingSubscriptions"
            :model="subscriptionModel"
            :resource-title="$t('interview.interview')"
            :translations="subscriberTranslations"
            @close="stopManagingSubscribers"
        />

        <popup-modal-confirm
            v-if="!!bulkApplicationData && interviewForm.send_confirmation_mail"
            confirm-button="success"
            :swap-button-logic="showCountWarning"
            :confirm-text="$t('general.confirm')"
            :cancel-text="showCountWarning ? $t('interview.extra_time_slot') : $t('general.cancel')"
            :message="showCountWarning ? $t('mailer.more_candidates_then_timeslots') + $tc('mailer.confirmation', bulkCount, {count: bulkCount}) : $tc('mailer.confirmation', bulkCount, {count: bulkCount}) "
            :title="showCountWarning ? $t('jobsite.browser_outdated.title') : ''"
            :working="performingBulkRequest"
            @close="cancelSend"
            @confirm="sendInvitation"
        />

        <popup-modal-confirm
            v-if="deletingAttachment"
            :confirmText="$t('general.delete_subject', {subject: $t('general.attachment')})"
            :message="$t('general.confirm_delete_subject', {subject: deletingAttachment.original_name})"
            :working="performingDeleteAttachmentRequest"
            @close="cancelDeleteAttachment"
            @confirm="deleteAttachment(deletingAttachment)"
        >
        </popup-modal-confirm>
    </popup-modal>
</template>

<script>
import PopupModal from './PopupModal.vue';
import Form from '../../etc/forms/Form';
import Input from '../forms/Input.vue';
import Wysiwyg from '../forms/Wysiwyg.vue';
import * as templates from '../../services/EmailTemplateService';
import * as signatures from '../../services/EmailSignatureService';
import * as interviews from '../../services/InterviewService';
import * as invitations from '../../services/InvitationService';
import {normalizeJson, buildDropdownOptions, openableFileExtensionsInWebBrowser} from '../../../shared/helpers';
import DatePicker from '../forms/DatePicker.vue';
import SimpleButton from './SimpleButton.vue';
import moment from 'moment';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useCompaniesStore} from '../../state/modules/companies';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Loading from './Loading.vue';
import Interview from '../../models/Interview';
import Invitation from '../../models/Invitation';
import Toggle from '../forms/Toggle.vue';
import SvgIcon from './SvgIcon.vue';
import SubscriberManager from './SubscriberManager';
import Tooltip from '../navigation/Tooltip';
import Subscription from '../../models/Subscription';
import Avatar from './Avatar';
import SelectDropdown from '../forms/SelectDropdown';
import InterviewIcons from '../InterviewIcons';
import InterviewTypeFields from '../InterviewTypeFields';
import InterviewTimeslots from '../InterviewTimeslots';
import EmailSignature from '../../models/EmailSignature';
import Tabs from '../navigation/Tabs';
import * as bulkApplicationKey from '../../services/BulkApplicationKeyService';
import PopupModalConfirm from './PopupModalConfirm.vue';
import EmailTemplate from '../../models/EmailTemplate';
import BccFields from './BccFields.vue';
import FileUpload from '../forms/FileUpload.vue';
import * as documents from '../../services/DocumentService';
import Document from '../../models/Document';
import Checkbox from "../forms/Checkbox";
import CalendarSyncEdit from './CalendarSyncEdit.vue';
import InterviewModeManager from "../interviews/InterviewModeManager.vue";
import AvailabilityScheduler from "../calendar/AvailabilityScheduler.vue";
import Recipients from "../mails/forms/Recipients.vue";
import sendsMails from "../../mixins/sendsMails";
import Banner from "./Banner.vue";
import Translatable from "../forms/Translatable.vue";
import ActionButton from "./ActionButton.vue";

export default {
    components: {
        ActionButton,
        Translatable,
        Banner,
        Recipients,
        AvailabilityScheduler,
        InterviewModeManager,
        CalendarSyncEdit,
        FileUpload,
        BccFields,
        PopupModalConfirm,
        Tabs,
        InterviewTimeslots,
        InterviewTypeFields,
        InterviewIcons,
        SelectDropdown,
        Avatar,
        Tooltip,
        SubscriberManager,
        Checkbox,
        'datepicker': DatePicker,
        'loading': Loading,
        'popup-modal': PopupModal,
        'simple-button': SimpleButton,
        'simple-input': Input,
        'toggle': Toggle,
        'wysiwyg': Wysiwyg,
        'svg-icon': SvgIcon,
    },
    mixins: [sendsMails],
    data: function () {
        return {
            subTypes: ['required', 'optional'],

            fetchingTemplates: true,
            sendingInvitation: false,
            showInternalNotes: false,
            showConfirmationForm: false,
            mailForm: this.generateMailForm(),
            interviewForm: this.generateInterviewForm(),
            invitationForm: this.generateInvitationForm(),
            selectedTemplate: 0,
            templateLocale: this.applications[0].locale,

            hasAccessToCalendar: false,

            managingSubscriptions: false,
            subscriptionModel: 'App\\Models\\Subscription',
            assignedPeople: [],
            selectedSignature: 0,

            fetchingData: false,
            performingBulkRequest: false,
            bulkApplicationData: null,
            bulkKeyDataForCount: null,

            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
            extensions: openableFileExtensionsInWebBrowser,
        };
    },
    computed: {
        ...mapState(useAuthenticationStore, ['defaultSignature']),
        user() {
            return this.currentUser;
        },
        submitButtonLabel() {
            return this.interviewForm.send_confirmation_mail ? this.$t('general.send') : this.$t('general.save_subject', {subject: this.$t('interview.interview')});
        },
        typeIsOnline() {
            return ['zoom', 'google', 'ms-teams', 'online'].includes(this.interviewForm.type);
        },
        closeOnClick() {
            return this.templates ? !this.mailForm.isDirty() && !this.interviewForm.isDirty() : true;
        },
        subscriptions() {
            return useRepo(Subscription)
                .with('user')
                .where('subable_type', this.subscriptionModel)
                .where('subable_id', null)
                .get();
        },
        requiredSubscriptions() {
            return this.subscriptions.filter(sub => sub.required);
        },
        optionalSubscriptions() {
            return this.subscriptions.filter(sub => !sub.required);
        },
        timeslotsCount() {
            return this.interviewForm.data.time_slots.length;
        },
        timeslotsAlerts() {
            return {
                countAlert: this.totalApplications > this.timeslotsCount,
            }
        },
        showCountWarning() {
            return !this.interviewForm.calendar_open && this.bulkCount > this.timeslotsCount;
        },
        subscriberTranslations() {
            return {
                add_myself: this.$t('interview.subs.add_myself'),
                added_myself: this.$t('interview.subs.added_myself'),
                description: this.$t('interview.subs.description'),
                remove_myself: this.$t('interview.subs.remove_myself'),
                removed_myself: this.$t('interview.subs.removed_myself'),
                subscribe: this.$t('interview.subs.subscribe'),
                subscribed_title: this.$t('interview.subs.subscribed_title'),
                title: this.$t('interview.subs.title'),
                unsubscribe: this.$t('interview.subs.unsubscribe'),
                unsubscribed_title: this.$t('interview.subs.unsubscribed_title'),
            };
        }
    },
    methods: {
        ...mapActions(useCompaniesStore, [ 'hasFeature' ]),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        subscriptionList(type) {
            if (type === 'required') {
                return this.requiredSubscriptions;
            } else {
                return this.optionalSubscriptions;
            }
        },
        generateMailForm() {
            return new Form([
                {name: 'to', value: ''},
                {name: 'bcc', value: ''},
                {name: 'subject', value: ''},
                {name: 'message', value: '<p></p>'},
                {name: 'attachments', value: []},
            ]);
        },
        generateInterviewForm() {
            let date = moment().add(1, 'w').set({second:0,millisecond:0}),
                remainder = 30 - (date.minute() % 30);

            if (remainder >= 15) {
                date = date.subtract(date.minute() % 30, 'm');
            } else {
                date = date.add(remainder, 'm');
            }

            return new Form([
                {name: 'name', value: null},
                {name: 'type', value: null},
                {name: 'internal_notes', value: null},
                {name: 'company_location_id', value: null},
                {name: 'street', value: null},
                {name: 'postal_code', value: null},
                {name: 'city', value: null},
                {name: 'country_id', value: null},
                {name: 'meeting_url', value: null},
                {name: 'duration', value: 30},
                {name: 'slot_confirmed', value: false},
                {name: 'send_confirmation_mail', value: true},
                {
                    name: 'time_slots', value: [{
                        id: null,
                        date: date,
                        available: true
                    }],
                },
                {name: 'sync', value: true},
                {name: 'mode', value: 'manually'},
                {name: 'calendar_open', value: false},
                {name: 'calendar_time_range', value: null},
            ]);
        },
        generateInvitationForm() {
            return new Form([
                {name: 'confirmation_subject', value: ''},
                {name: 'confirmation_message', value: '<p></p>'},
            ]);
        },
        getBulkApplicationKey() {
            this.sendingInvitation = true;

            return this.mailForm.submit(data => bulkApplicationKey.get({
                data: {
                    selection: {
                        applications: this.applications.map(({id}) => id),
                        stages: this.stages,
                    },
                },
            }, this.requestFilters)).then(data => {
                this.bulkApplicationData = data;

                if (!this.interviewForm.send_confirmation_mail) {
                    return this.sendInvitation();
                }
            });
        },
        sendInvitation() {
            this.sendingInvitation = true;
            this.performingBulkRequest = true;

            const invitationData = this.invitationForm.data;

            this.interviewForm.submit(data => {
                if (this.typeIsOnline) {
                    let meetingUrl = data.meeting_url;
                    if (meetingUrl && meetingUrl.indexOf('https') !== 0 && meetingUrl.indexOf('http') === 0) {
                        meetingUrl = meetingUrl.replace('http', 'https');
                    } else if (meetingUrl && meetingUrl.indexOf('https') !== 0 && meetingUrl.indexOf('http') !== 0) {
                        meetingUrl = 'https://' + meetingUrl;
                    }
                    data.meeting_url = meetingUrl;
                }

                return interviews.createBulk({
                    data: {
                        bulkApplicationId: this.bulkApplicationData.id,
                        attributes: data,
                        options: this.interviewForm.options,
                        relationships: {
                            user: {
                                data: {
                                    type: 'users',
                                    id: this.currentUserId,
                                },
                            },
                            subscriptions: this.subscriptions.map(sub => ({
                                data: {
                                    type: 'subscriptions',
                                    user_id: sub.user_id,
                                    required: sub.required
                                },
                            })),
                            time_slots: {
                                update: !this.interviewForm.slot_confirmed ? data.time_slots : [data.time_slots[0]],
                            },
                            invitation: {
                                data: {
                                    bcc: this.mailForm.bcc,
                                    subject: this.mailForm.subject,
                                    message: this.mailForm.message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                                    attachments: this.mailForm.attachments,
                                    confirmation_message: invitationData.confirmation_message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                                    confirmation_subject: invitationData.confirmation_subject,
                                    duration: invitationData.duration,
                                },
                            },
                        },
                    },
                });
            }).then(result => {
                if (result?.data?.message === 'started') {
                    this.$emit('close');
                    return this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('bulkaction.interview.storebulk.started'),
                    });
                }
                return useRepo(Interview).save(normalizeJson(result));
            }).then(async inserts => {
                if (!inserts) return;

                let status = await this.sendInvitationForm(inserts)
                    .then(mail => {
                        if (!this.interviewForm.slot_confirmed) {
                            // store invitation mail
                            return useRepo(Invitation).save(normalizeJson(mail));
                        }
                        return mail;
                    }).then(data => {
                        if (!this.interviewForm.slot_confirmed) {
                            // show invitation notification
                            let subject = this.totalApplications > 1 ? this.$t('applications.selected_applications') : this.applications[0].fullName;

                            this[ADD_NOTIFICATION]({
                                type: 'success',
                                message: this.$tc('mailer.invitation_success', this.totalApplications, { subject }),
                            });
                        }

                        return data;
                    }).catch(errors => {
                        this.catchErrors(errors);
                    });

                if (status) {
                    if (this.interviewForm.slot_confirmed) {
                        if (this.interviewForm.send_confirmation_mail) {
                            // it's not possible to confirm slots in bulk, so there will always be one interview
                            const interview = inserts[0];
                            return interviews.confirmInvitation(interview.id, {
                                bcc: this.mailForm.bcc,
                                confirmation_subject: this.invitationForm.data.confirmation_subject,
                                confirmation_message: this.invitationForm.data.confirmation_message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                            }).then(() => {
                                let subject = this.totalApplications > 1 ? this.$t('applications.selected_applications') : this.applications[0].fullName;
                                this[ADD_NOTIFICATION]({
                                    type: 'success',
                                    message: this.$tc('mailer.confirmation_success', this.totalApplications, { subject }),
                                });
                                this.$emit('close');
                            });
                        } else {
                            this.$emit('close');
                        }
                    } else {
                        this.$emit('close');
                    }
                }
            }).catch(errors => {
                this.catchErrors(errors);
            }).finally(() => {
                this.sendingInvitation = false;
                this.performingBulkRequest = false;
            });
        },
        sendInvitationForm(inserts) {
            return this.mailForm.submit(data => invitations.storeBulk({
                data: {
                    bulkApplicationId: this.bulkApplicationData.id,
                    attributes: {
                        bcc: data.bcc,
                        subject: data.subject,
                        message: data.message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                        attachments: data.attachments,
                    },
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: this.currentUserId,
                            },
                        },
                        interviewInvitations: inserts.map(item => ({
                            data: {
                                type: 'interview-invitations',
                                interview_id: item.invitation.interview_id,
                                id: item.invitation.id,
                            },
                        })),
                    },
                },
            }));
        },
        manageSubscriptions() {
            this.managingSubscriptions = true;
        },
        stopManagingSubscribers() {
            this.managingSubscriptions = false;
        },
        removeFile({rawFile}) {
            if (!rawFile.id) {
                return;
            }

            documents.destroy(rawFile.id)
                .then(() => useRepo(Document).destroy(rawFile.id));
        },
        //Show popup to remove attachments added by default
        confirmDeleteAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment(attachment) {
            this.performingDeleteAttachmentRequest = true;

            //Remove from list, not from default template
            this.mailForm.attachments = this.mailForm.attachments.filter(att => att.id !== attachment.id);

            this.deletingAttachment = null;
            this.performingDeleteAttachmentRequest = false;
        },
        hydrateFromTemplate(data) {
            let lang = isNaN(data) ? data : this.active_locale;
            let templateId = !isNaN(data) ? data : this.selectedTemplate;

            this.templateLocale = lang;

            let template = this.templates.find(template => template.id === templateId);
            //Always empty attachments
            this.mailForm.attachments = [];

            if (typeof template != 'undefined') {
                //Check for attachments
                if (template.attachments.length > 0) {
                    //Add attachments to form by locale
                    this.mailForm.attachments = template.attachments.filter(obj => obj.locale === this.active_locale);
                }

                this.mailForm.subject = template.subject[this.templateLocale];
                this.$refs.content.setContent(template.content[this.templateLocale]);
                this.invitationForm.confirmation_subject = template.confirmation_subject[this.templateLocale];
                this.$refs.confirmation_content.setContent(template.confirmation_content[this.templateLocale]);
            }
        },
        getCount() {
            this.mailForm.submit(data => bulkApplicationKey.get({
                    data: {
                        selection: {
                            applications: this.applications,
                            stages: this.stages,
                        },
                        relationships: {
                            stage: {
                                data: {
                                    type: 'stages',
                                    id: data.stage,
                                },
                            },
                        },
                    },
                }, this.requestFilters),
            ).then(data => this.bulkKeyDataForCount = data)
                .finally(() => {
                    this.fetchingData = false;
                });
        },
        catchErrors(error) {
            if (error.response?.status === 422 && error.response?.data.errors) {
                Object.keys(this.invitationForm.errors).forEach(field => {
                    this.invitationForm.errors[field] = error.response.data.errors[field] ||
                        error.response.data.errors['data.relationships.invitation.data.' + field] ||
                        undefined;
                });
            }
            if (this.invitationForm.errors.confirmation_message || this.invitationForm.errors.confirmation_subject) {
                this.showConfirmationForm = true;
            }

            this.performingBulkRequest = false;
            this.cancelSend();
        },
        cancelSend() {
            this.bulkApplicationData = null;
            this.sendingInvitation = false;
        },
        setAccessToFeature() {
            return new Promise(resolve => {
                this.hasAccessToCalendar = !!this.hasFeature('calendar')
                resolve();
            });
        },
    },
    watch: {
        'interviewForm.calendar_open'() {
            if (this.interviewForm.calendar_open) {
                this.interviewForm.time_slots = [];
            } else {
                this.interviewForm.calendar_time_range = null;
            }
        },
        'interviewForm.slot_confirmed'() {
            this.showConfirmationForm = !!this.interviewForm.slot_confirmed;
        },
        'interviewForm.mode'() {
            if (this.interviewForm.mode === 'manually' && !this.interviewForm.time_slots.length) {
                let date = moment().add(1, 'w').set({second:0,millisecond:0}),
                    remainder = this.interviewForm.duration - (date.minute() % this.interviewForm.duration);

                if (remainder >= 15) {
                    date = moment(date).subtract(date.minute() % 30, 'm');
                } else {
                    date = moment(date).add(remainder, 'm');
                }

                this.interviewForm.time_slots = [{
                    id: null,
                    date: date,
                    available: true
                }];
            }
        }
    },
    mounted() {
        this.fetchingTemplates = true;

        useRepo(Subscription).save({
                id: this.subscriptionModel + this.user.id,
                user_id: this.currentUserId,
                company_id: this.currentCompanyId,
                subable_id: null,
                subable_type: this.subscriptionModel,
                required: true,
        });

        signatures.index().then(result => {
            return useRepo(EmailSignature).save(normalizeJson(result.data));
        }).then(() => {
            this.selectedSignature = this.defaultSignature?.id || 0;
        });

        templates.index().then(result => {
            return useRepo(EmailTemplate).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingTemplates = false;
        });

        if (!this.applications.length) {
            this.fetchingData = true;
            this.getCount();
        }

        this.setAccessToFeature()
            .then(() => {
                if (this.hasAccessToCalendar && this.user) {
                    this.interviewForm.mode = this.user.primary_calendar_id ? 'calendar' : 'manually'
                    this.interviewForm.sync = this.user.cronofy_auto_sync;

                    if (this.interviewForm.mode === 'calendar') {
                        this.interviewForm.time_slots = [];
                    }
                }
            });


    },
    beforeUnmount() {
        useRepo(Subscription)
            .where((sub) => {
                return sub.subable_type === this.subscriptionModel && !sub.subable_id;
            })
            .delete();
    },
};
</script>
